/**
 * Category Priorities Table Component
 *
 * Displays category-level AEO priorities with product aggregation,
 * coverage gaps, and AI visibility impact projections.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  FolderTree, Download, Bot, ChevronLeft, ChevronRight, Info,
  ChevronDown, ChevronUp, Package, Sparkles, TrendingUp
} from 'lucide-react';

import {
  getCategoryPriorities,
  exportPriorities,
  downloadCsv,
  CategoryPriority,
} from '@/services/aeoPrioritiesService';

interface Props {
  siteId: string;
}

export function CategoryPrioritiesTable({ siteId }: Props) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [priorities, setPriorities] = useState<CategoryPriority[]>([]);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [excludeEnriched, setExcludeEnriched] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPriorities();
  }, [siteId, page, excludeEnriched]);

  const loadPriorities = async () => {
    setLoading(true);
    try {
      const result = await getCategoryPriorities(siteId, {
        limit,
        offset: page * limit,
        excludeEnriched,
      });
      setPriorities(result.priorities);
    } catch (err) {
      console.error('Failed to load category priorities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPriorities(siteId, 'category');
      downloadCsv(blob, `aeo-category-priorities-${siteId}.csv`);
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setExporting(false);
    }
  };

  const toggleRow = (category: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (loading && priorities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Category Priorities
            </CardTitle>
            <CardDescription className="mt-1">
              Categories ranked by potential AEO impact. Focus bulk enrichment efforts on high-priority categories.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="excludeEnrichedCategories"
              checked={excludeEnriched}
              onCheckedChange={(checked) => setExcludeEnriched(checked === true)}
            />
            <label htmlFor="excludeEnrichedCategories" className="text-sm cursor-pointer">
              Hide fully enriched
            </label>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30px]"></TableHead>
              <TableHead className="w-[25%]">Category</TableHead>
              <TableHead className="w-[100px] text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1 justify-center">
                      Score
                      <Info className="h-3 w-3" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold mb-1">Category Score Components:</p>
                      <ul className="text-xs space-y-1">
                        <li>25% Bot Visits (aggregated)</li>
                        <li>25% AI Bot Share</li>
                        <li>15% Product Count</li>
                        <li>20% Coverage Gap</li>
                        <li>5% Sitemap Position</li>
                        <li>10% Revenue Potential</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-center">Products</TableHead>
              <TableHead className="text-center">URLs</TableHead>
              <TableHead className="text-center">Bot Visits</TableHead>
              <TableHead className="text-center">Coverage</TableHead>
              <TableHead className="text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1 justify-center">
                      AI Impact
                      <Info className="h-3 w-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Potential AI visibility increase if this category is fully enriched
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {priorities.map((priority) => (
              <>
                <TableRow
                  key={priority.category}
                  className={`cursor-pointer hover:bg-muted/50 ${loading ? 'opacity-50' : ''}`}
                  onClick={() => toggleRow(priority.category)}
                >
                  <TableCell className="w-[30px]">
                    {expandedRows.has(priority.category) ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="font-medium">{priority.category}</div>
                    {priority.subcategory && (
                      <div className="text-xs text-muted-foreground">{priority.subcategory}</div>
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col items-center gap-1">
                      <span className="font-bold text-lg">{priority.priorityScore.toFixed(1)}</span>
                      <Progress
                        value={priority.priorityScore}
                        className="h-1.5 w-16"
                      />
                    </div>
                  </TableCell>

                  <TableCell className="text-center">
                    <Badge variant="secondary" className="gap-1">
                      <Package className="h-3 w-3" />
                      {priority.metrics.totalProducts}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-center">
                    <span className="text-sm">{priority.metrics.totalUrls.toLocaleString()}</span>
                  </TableCell>

                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Bot className="h-3 w-3 text-muted-foreground" />
                      <span>{priority.metrics.totalBotVisits30d.toLocaleString()}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2">
                            <Progress
                              value={priority.metrics.enrichmentCoveragePct}
                              className="h-2 w-16"
                            />
                            <span className="text-sm font-medium">
                              {priority.metrics.enrichmentCoveragePct.toFixed(0)}%
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {priority.metrics.enrichedProducts} of {priority.metrics.totalProducts} products enriched
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>

                  <TableCell className="text-center">
                    {priority.impact.potentialAiVisibilityIncrease > 0 ? (
                      <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        +{priority.impact.potentialAiVisibilityIncrease.toFixed(1)}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>

                {/* Expanded content with top products */}
                {expandedRows.has(priority.category) && priority.topProducts.length > 0 && (
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={8} className="py-4">
                      <div className="pl-8">
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-orange-500" />
                          Top Products to Enrich
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {priority.topProducts.map((product, i) => (
                            <div
                              key={product.name}
                              className="flex items-center justify-between bg-background rounded-md px-3 py-2 border"
                            >
                              <div className="truncate flex-1 mr-2">
                                <span className="text-xs text-muted-foreground mr-2">#{i + 1}</span>
                                <span className="text-sm">{product.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {product.urls} URLs
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {product.score.toFixed(1)}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}

            {priorities.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No category priorities found. Categories are identified from page semantic analysis.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {priorities.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {page * limit + 1} - {page * limit + priorities.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={priorities.length < limit || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
